import hr from 'date-fns/locale/hr'
import { useEffect, useRef, useState } from 'react'
import ReactDatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

import { cn } from '../../utils'

import { getPickerClassName, getPopperClassName } from './DatePicker.styles'
import { DatePickerHeader } from './DatePickerHeader'
import { DatePickerInput } from './DatePickerInput'
import { DatePickerProps } from './types'

import './DatePicker.css'
import { TimeInput } from './TimeInput'

import de from 'date-fns/locale/de'
import en from 'date-fns/locale/en-GB'
import es from 'date-fns/locale/es'
import fr from 'date-fns/locale/fr'
import it from 'date-fns/locale/it'
import pt from 'date-fns/locale/pt'
import { Label } from '../Label'

/**
 *
 * @param handleDate - Callback function to get the selected date
 * @param className - className for the DatePicker
 * @param dayClassName - className for the DatePicker day
 * @param weekClassName - className for the DatePicker week
 * @param showTimeSelectOnly - Show only the time picker
 * @param showTimeSelect - Show the time picker
 * @param dateFormat - Date format for the DatePicker
 * @param timeFormat - Time format without date
 * @param showArrow - Show the arrow on the DatePicker
 * @param value - Value of the DatePicker
 * @param minDate - Minimum date for the DatePicker
 * @param placeholder - Placeholder for the DatePicker
 */

const locales: Record<string, Locale> = { en, hr, fr, de, it, es, pt }
Object.entries(locales).forEach(([key, locale]) => registerLocale(key, locale))

export const DatePicker = ({
    dateFormat = 'dd.MM.yyyy HH:mm',
    timeFormat = 'HH:mm',
    locale = 'hr',
    showTimeSelectOnly = false,
    showTimeSelect = false,
    handleDate,
    className,
    dayClassName,
    weekClassName,
    showArrow = false,
    value,
    placeholder,
    disabled = false,
    t,
    label = '',
    labelSize = 'md',
    labelColor = 'secondary',
    labelVariant = 'text',
}: DatePickerProps) => {
    // Convert the value to a Date object if it's a string
    const initialDate = value
        ? typeof value === 'string'
            ? new Date(value)
            : value
        : null
    const [startDate, setStartDate] = useState<Date | null>(initialDate)

    // Initialize time values based on the initial date
    const initialHours = initialDate ? initialDate.getHours().toString() : ''
    const initialMinutes = initialDate
        ? initialDate.getMinutes().toString()
        : ''
    const [timeValues, setTimeValues] = useState({
        hours: initialHours,
        minutes: initialMinutes,
        inputValue: '',
    })

    const inputRef = useRef<HTMLInputElement>(null)
    const selectedLocale = locales[locale] ? locale : 'hr'

    // Update the startDate when the value prop changes
    useEffect(() => {
        if (value) {
            const newDate = typeof value === 'string' ? new Date(value) : value
            setStartDate(newDate)

            if (newDate) {
                setTimeValues({
                    hours: newDate.getHours().toString(),
                    minutes: newDate.getMinutes().toString(),
                    inputValue: timeValues.inputValue,
                })
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- unchanged deps keep current behaviour, see UNIS-105
    }, [value])

    const handleTimeChange = (hours: number, minutes: number) => {
        const newDate = startDate || new Date()
        newDate.setHours(hours)
        newDate.setMinutes(minutes)
        setStartDate(newDate)
        setTimeValues({
            hours: String(hours),
            minutes: String(minutes),
            inputValue: timeValues.inputValue,
        })
        handleDate?.(newDate)
    }

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = e.target.value
        setTimeValues({ ...timeValues, inputValue })
    }

    return (
        <div className="flex w-full flex-col gap-0.5">
            <Label
                text={label || ''}
                variant={labelVariant}
                color={labelColor}
                size={labelSize}
                htmlFor={label}
            />
            <ReactDatePicker
                popperPlacement="top"
                disabled={disabled}
                placeholderText={placeholder}
                selected={startDate}
                onChange={(date: Date) => {
                    const newDate = date
                    if (timeValues.hours || timeValues.minutes) {
                        newDate.setHours(parseInt(timeValues.hours) || 0)
                        newDate.setMinutes(parseInt(timeValues.minutes) || 0)
                    }
                    setStartDate(newDate)
                    handleDate?.(newDate)
                }}
                customTimeInput={
                    <TimeInput
                        date={startDate}
                        hours={timeValues.hours}
                        minutes={timeValues.minutes}
                        t={t}
                        onChangeCustom={handleTimeChange}
                    />
                }
                customInput={
                    <DatePickerInput
                        placeholder={placeholder}
                        value={timeValues.inputValue}
                        onChange={handleInputChange}
                        ref={inputRef}
                        isTime={showTimeSelectOnly}
                        disabled={disabled}
                    />
                }
                wrapperClassName="flex w-full h-full cursor-pointer bg-background-base"
                popperClassName={cn(getPopperClassName(), className)}
                autoFocus={false}
                locale={selectedLocale}
                dateFormat={showTimeSelectOnly ? timeFormat : dateFormat}
                showTimeInput={showTimeSelect || showTimeSelectOnly}
                showPopperArrow={showArrow}
                showTimeSelectOnly={showTimeSelectOnly}
                dayClassName={() => cn(getPickerClassName(), dayClassName)}
                weekDayClassName={() =>
                    cn(
                        getPickerClassName(),
                        weekClassName,
                        'hover::bg-neutral-100'
                    )
                }
                renderCustomHeader={({
                    date,
                    decreaseMonth,
                    increaseMonth,
                    changeMonth,
                    changeYear,
                }) => (
                    <DatePickerHeader
                        date={date}
                        decreaseMonth={decreaseMonth}
                        increaseMonth={increaseMonth}
                        changeMonth={changeMonth}
                        changeYear={changeYear}
                        t={t}
                    />
                )}
            />
        </div>
    )
}
